import React, { ReactElement, useMemo } from 'react';

import { Box, Button, IconButton } from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import Edit from '@material-ui/icons/Edit';

import { ComparisonAndProjectOverview } from '..';
import { Bundle } from '../../../redux';
import { Body1, Header2 } from '../../atoms';
import { ProjectComparisonDto, ProjectDto } from '../../../models';

import './bundle-viewer.styles.scss';
import { useTranslation } from 'react-i18next';

export interface IBundleViewerProps {
	disabled?: boolean;
	bundle: Bundle;
	comparisons: ProjectComparisonDto[];
	projects: ProjectDto[];
	disableEditing?: boolean;
	selectable?: boolean;
	onComparisonClick?: (dto: ProjectComparisonDto) => void;
	onComparisonChangeBundle?: (dto: ProjectComparisonDto) => void;
	onComparisonRemove?: (dto: ProjectComparisonDto) => void;
	onComparisonUpdate?: (dto: ProjectComparisonDto) => void;
	onProjectClick?: (dto: ProjectDto) => void;
	onProjectPreview?: (dto: ProjectDto) => void;
	onProjectChangeBundle?: (dto: ProjectDto) => void;
	onProjectRemove?: (dto: ProjectDto) => void;
	onProjectUpdate?: (dto: ProjectDto) => void;
	onAddContent?: () => void;
	onEditBundleClick?: () => void;
	isProjectSelected?: (dto: ProjectDto) => boolean;
}

export function BundleViewer(props: IBundleViewerProps): ReactElement {
	const {
		disabled,
		bundle,
		projects,
		comparisons,
		disableEditing,
		selectable,
		isProjectSelected,
		onComparisonClick,
		onComparisonRemove,
		onComparisonChangeBundle,
		onComparisonUpdate,
		onProjectChangeBundle,
		onProjectClick,
		onProjectPreview,
		onProjectRemove,
		onProjectUpdate,
		onAddContent,
		onEditBundleClick
	} = props;

	const { t } = useTranslation('myBosatlas');

	const contentAmount: number = useMemo<number>(() => {
		return projects.length + comparisons.length;
	}, [projects, comparisons]);

	return (
		<Box className="bundle-viewer">
			<Box className="header">
				<Box className="title-and-edit-button">
					<Header2>{bundle.title}</Header2>
					{!disableEditing && (
						<IconButton color="primary" onClick={onEditBundleClick}>
							<Edit />
						</IconButton>
					)}
				</Box>
				{!disableEditing && (
					<Box>
						<Button
							variant="outlined"
							color="primary"
							onClick={onAddContent}
						>
							<Add />
							{t('folder.addMapFolder')}
						</Button>
					</Box>
				)}
			</Box>
			{bundle.projects && bundle.comparisons && contentAmount === 0 && (
				<Box className="add-content-box">
					<Body1>{t('folder.noMapsAdded')}</Body1>
					{!disableEditing && (
						<Button
							onClick={onAddContent}
							variant="outlined"
							color="primary"
							className="add-button"
						>
							<Add />
							{t('folder.addMap')}
						</Button>
					)}
				</Box>
			)}
			{bundle.projects && bundle.comparisons && contentAmount > 0 && (
				<ComparisonAndProjectOverview
					disabled={disabled}
					showInFolder
					disableEditing={disableEditing}
					selectable={selectable}
					projects={projects}
					comparisons={comparisons}
					onComparisonClick={onComparisonClick}
					onComparisonChangeBundle={onComparisonChangeBundle}
					onComparisonRemove={onComparisonRemove}
					onComparisonUpdate={onComparisonUpdate}
					onProjectClick={onProjectClick}
					onProjectChangeBundle={onProjectChangeBundle}
					onProjectRemove={onProjectRemove}
					onProjectUpdate={onProjectUpdate}
					onProjectPreview={onProjectPreview}
					isProjectSelected={isProjectSelected}
				/>
			)}
		</Box>
	);
}
